/**
 * Shared status display utilities for ATM freshness/decay model.
 */

const STATUS_LABELS = {
  working: "Working",
  out_of_service: "Out of Service",
  out_of_cash: "Out of Cash",
  unknown: "Unknown",
};

const STATUS_COLORS = {
  working: "#00FF94",
  out_of_service: "#FF3B30",
  out_of_cash: "#FFCC00",
  unknown: "#71717A",
};

/**
 * Returns { label, timeAgo, freshness, opacity, color } for a given ATM object.
 * freshness: "fresh" | "recent" | "aging" | "stale" | "old" | "ancient" | "none"
 */
export function getStatusDisplay(atm) {
  const status = atm?.status || "unknown";
  const label = STATUS_LABELS[status] || "Unknown";
  const color = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  const lastReport = atm?.last_report_time;

  if (!lastReport) {
    return { label: "No Reports", timeAgo: null, freshness: "none", opacity: 0.35, color: STATUS_COLORS.unknown };
  }

  const now = new Date();
  const reportDate = new Date(lastReport);
  const diffMs = now - reportDate;
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffHours / 24;

  let timeAgo = null;
  let freshness = "fresh";
  let opacity = 1;

  if (diffHours < 1) {
    freshness = "fresh";
    opacity = 1;
    timeAgo = null; // No suffix needed — it's live
  } else if (diffHours < 6) {
    freshness = "recent";
    opacity = 0.9;
    timeAgo = `${Math.floor(diffHours)}h ago`;
  } else if (diffHours < 24) {
    freshness = "aging";
    opacity = 0.75;
    timeAgo = `${Math.floor(diffHours)}h ago`;
  } else if (diffDays < 3) {
    freshness = "stale";
    opacity = 0.6;
    timeAgo = `${Math.floor(diffDays)}d ago`;
  } else if (diffDays < 7) {
    freshness = "old";
    opacity = 0.45;
    timeAgo = `${Math.floor(diffDays)}d ago`;
  } else if (diffDays < 30) {
    freshness = "ancient";
    opacity = 0.35;
    const weeks = Math.floor(diffDays / 7);
    timeAgo = `${weeks}w ago`;
  } else {
    freshness = "ancient";
    opacity = 0.3;
    const months = Math.floor(diffDays / 30);
    timeAgo = `${months}mo ago`;
  }

  const displayLabel = freshness === "ancient" ? `Last seen ${label.toLowerCase()}` : label;

  return { label: displayLabel, timeAgo, freshness, opacity, color };
}

/**
 * Renders a status badge string: "Working · 3h ago" or just "Working" if fresh.
 */
export function formatStatusText(atm) {
  const { label, timeAgo } = getStatusDisplay(atm);
  if (!timeAgo) return label;
  return `${label} · ${timeAgo}`;
}
